import IntegrationEditorStepLogsTableVirtualized from "./IntegrationEditorStepLogsTableVirtualized";
import {
  BlueprintRunnerStepLogEntryType,
  BlueprintRunnerStepLogs,
} from "../../../models/Blueprints";
import { useMemo, useState } from "react";
import { Button, ButtonVariant, Text, Typeahead } from "@merge-api/merge-javascript-shared";
import { ChevronDown, ChevronUp } from "lucide-react";
import { getDifferenceInDatesInMilliseconds } from "../../../utils";

interface Props {
  startTime: string;
  stepLogs: BlueprintRunnerStepLogs;
  reverse?: boolean;
}

const ENTRY_TYPE_OPTIONS = Object.values(BlueprintRunnerStepLogEntryType).map((v) => ({
  label: v,
  value: v,
}));

const IntegrationEditorStepLogsTable = ({ startTime, stepLogs, reverse = false }: Props) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [minSeconds, setMinSeconds] = useState<string>("");
  const [maxSeconds, setMaxSeconds] = useState<string>("");
  const [stepIdFilter, setStepIdFilter] = useState<string | null>(null);
  const [entryTypeFilter, setEntryTypeFilter] = useState<BlueprintRunnerStepLogEntryType | null>(
    null
  );

  // Derive unique step_ids present in logs for the dropdown
  const stepIdOptions = useMemo(() => {
    const ids = Array.from(
      new Set(stepLogs.map((log) => log.step_id).filter((id): id is string => id !== null))
    );
    return ids.map((id) => ({ label: id, value: id }));
  }, [stepLogs]);

  const filteredLogs = useMemo(() => {
    const minSec = minSeconds !== "" ? parseFloat(minSeconds) : null;
    const maxSec = maxSeconds !== "" ? parseFloat(maxSeconds) : null;

    return stepLogs.filter((log) => {
      // Time range filter
      if (minSec !== null || maxSec !== null) {
        const offsetSec = getDifferenceInDatesInMilliseconds(startTime, log.time) / 1000;
        if (minSec !== null && offsetSec < minSec) return false;
        if (maxSec !== null && offsetSec > maxSec) return false;
      }

      // step_id filter
      if (stepIdFilter !== null && log.step_id !== stepIdFilter) return false;

      // entry_type filter
      if (entryTypeFilter !== null && log.entry_type !== entryTypeFilter) return false;

      return true;
    });
  }, [stepLogs, startTime, minSeconds, maxSeconds, stepIdFilter, entryTypeFilter]);

  const displayedLogs = useMemo(() => (reverse ? [...filteredLogs].reverse() : filteredLogs), [
    filteredLogs,
    reverse,
  ]);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Filter controls */}
      <div className="flex flex-col border border-b-0 border-gray-10 bg-gray-0">
        <Button
          variant={ButtonVariant.SecondaryGray}
          onClick={() => setFiltersOpen((prev) => !prev)}
          fullWidth={true}
          rightIcon={filtersOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        >
          Filters
        </Button>

        {filtersOpen && (
          <div className="flex flex-col gap-2 px-2 pt-2 pb-2">
            {/* Row 1: Time range */}
            <div className="flex flex-row gap-2 items-end">
              <div className="flex flex-col gap-1 flex-1">
                <Text variant="sm" className="text-gray-60">
                  From (s)
                </Text>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={minSeconds}
                  onChange={(e) => setMinSeconds(e.target.value)}
                  placeholder="0.00"
                  className="border border-gray-20 rounded px-2 py-1 text-sm w-full focus:outline-none focus:border-blue-40"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <Text variant="sm" className="text-gray-60">
                  To (s)
                </Text>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={maxSeconds}
                  onChange={(e) => setMaxSeconds(e.target.value)}
                  placeholder="∞"
                  className="border border-gray-20 rounded px-2 py-1 text-sm w-full focus:outline-none focus:border-blue-40"
                />
              </div>
            </div>

            {/* Time range warning */}
            {minSeconds !== "" &&
              maxSeconds !== "" &&
              parseFloat(minSeconds) > parseFloat(maxSeconds) && (
                <Text variant="sm" className="text-red-50">
                  Warning: "From" time is greater than "To" time — no results will be shown.
                </Text>
              )}

            {/* Row 2: Step ID + Entry type */}
            <div className="flex flex-row gap-2 items-end">
              <div className="flex flex-col gap-1 flex-1">
                <Text variant="sm" className="text-gray-60">
                  Step ID
                </Text>
                <Typeahead
                  placeholder="All steps"
                  options={stepIdOptions}
                  value={stepIdFilter ? { label: stepIdFilter, value: stepIdFilter } : null}
                  getOptionLabel={(option: { label: string; value: string }) => option.label}
                  renderOption={(option: { label: string; value: string }) => (
                    <Text>{option.label}</Text>
                  )}
                  onChange={(_: any, val: { value: string } | null) =>
                    setStepIdFilter(val?.value ?? null)
                  }
                />
              </div>

              <div className="flex flex-col gap-1 flex-1">
                <Text variant="sm" className="text-gray-60">
                  Entry type
                </Text>
                <Typeahead
                  placeholder="All types"
                  options={ENTRY_TYPE_OPTIONS}
                  value={
                    entryTypeFilter ? { label: entryTypeFilter, value: entryTypeFilter } : null
                  }
                  getOptionLabel={(option: { label: string; value: string }) => option.label}
                  renderOption={(option: { label: string; value: string }) => (
                    <Text>{option.label}</Text>
                  )}
                  onChange={(_: any, val: { value: BlueprintRunnerStepLogEntryType } | null) =>
                    setEntryTypeFilter(val?.value ?? null)
                  }
                />
              </div>
            </div>

            <Text variant="sm" className="text-gray-60">
              {filteredLogs.length} / {stepLogs.length} log entries
            </Text>
          </div>
        )}
      </div>

      {/* Log table */}
      <div className="p-0 w-full flex-1 min-h-[1000px] border border-gray-10">
        <IntegrationEditorStepLogsTableVirtualized startTime={startTime} logs={displayedLogs} />
      </div>
    </div>
  );
};

export default IntegrationEditorStepLogsTable;
